import { useCallback, useSyncExternalStore } from "react";

export const personPreference = <T extends string>(prefix: string, values: readonly T[], fallback: T) => {
  const listeners = new Set<() => void>();
  const keyFor = (personId: string) => `${prefix}:${personId}`;

  const read = (personId: string): T => {
    try {
      const stored = localStorage.getItem(keyFor(personId));
      return values.find((value) => value === stored) ?? fallback;
    } catch {
      return fallback;
    }
  };

  const memory = new Map<string, T>();

  const persisted = (personId: string, value: T) => {
    try {
      localStorage.setItem(keyFor(personId), value);
      return true;
    } catch {
      return false;
    }
  };

  const write = (personId: string, value: T) => {
    if (persisted(personId, value)) memory.delete(personId);
    else memory.set(personId, value);
    listeners.forEach((listener) => listener());
  };

  const current = (personId: string) => memory.get(personId) ?? read(personId);

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const use = (personId: string): [T, (value: T) => void] => {
    const value = useSyncExternalStore(subscribe, () => current(personId));
    const set = useCallback((next: T) => write(personId, next), [personId]);
    return [value, set];
  };

  return { use, read: current, write };
};
